import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { AppLoading } from "@/components/AppState";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ArrowRight, Bot, BookOpen, MessagesSquare, Mic, Radio, ShieldCheck, Check } from "lucide-react";
import { API } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { LangToggle } from "@/components/common";

const HERO = "https://images.unsplash.com/photo-1660631228116-b3643559f611?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const DATA = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const FACE = "https://images.pexels.com/photos/11650978/pexels-photo-11650978.jpeg?auto=compress&cs=tinysrgb&w=900";

const Nav = () => {
  const { t, site } = useT();
  return (
    <header className="fixed top-0 inset-x-0 z-40 border-b border-white/[0.06] bg-zinc-950/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16">
        <span className="font-display font-extrabold text-lg tracking-tight">IAGORA <span className="text-indigo-400">AI LIVE</span></span>
        <div className="flex items-center gap-3">
          {site?.sections.some((s) => s.id === "pricing" && s.visible) && <a href="#pricing" className="hidden sm:inline text-sm text-slate-400 hover:text-white transition-colors" data-testid="nav-pricing-link">{t("pricing")}</a>}
          <LangToggle />
          <Link to="/sign-in" data-testid="nav-signin-btn" className="rounded-full bg-white text-zinc-950 px-4 py-2 text-sm font-semibold hover:bg-indigo-100 transition-colors">{t("sign_in")}</Link>
        </div>
      </div>
    </header>
  );
};

const Hero = ({ img }) => {
  const { t, site } = useT();
  return (
    <section className="relative min-h-[92vh] flex items-end grain overflow-hidden">
      <img src={img.hero || HERO} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-image" />
      <div className="absolute inset-0 bg-[#09090B]/75" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#09090B] to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-20 pt-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-600/10 px-3 py-1 text-xs text-rose-200 mb-8">
            <span className="live-dot h-2 w-2 rounded-full bg-rose-500" /> {t("hero_badge")}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
            {t("hero_title_1")} <span className="text-indigo-400">{t("hero_title_2")}</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 max-w-2xl font-light">{t("hero_sub")}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/sign-up" data-testid="hero-cta-btn" className="group inline-flex items-center gap-2 rounded-full bg-indigo-600 px-7 py-3.5 font-semibold shadow-[0_0_32px_rgba(79,70,229,0.45)] hover:bg-indigo-500 transition-colors">
              {t("hero_cta")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            {site?.sections.some((s) => s.id === "features" && s.visible) && <a href="#features" className="inline-flex items-center rounded-full border border-white/15 px-7 py-3.5 text-slate-200 hover:bg-white/5 transition-colors" data-testid="hero-secondary-btn">{t("hero_secondary")}</a>}
          </div>
        </div>
        <div className="lg:col-span-4 self-end fade-up" style={{ animationDelay: "200ms" }}>
          <div className="rounded-lg border border-white/10 bg-zinc-950/70 backdrop-blur-xl p-5 beam">
            <div className="flex items-center gap-3 mb-4">
              <img src={img.avatar || FACE} alt="" className="h-10 w-10 rounded-full object-cover speaking-ring" />
              <div><p className="font-display font-bold">Nova</p><p className="text-xs text-slate-400">{t("hero_card_role")}</p></div>
            </div>
            <p className="text-sm text-slate-400"><span className="text-slate-200">@marco_92:</span> {t("hero_card_q")}</p>
            <p className="text-sm mt-3 rounded-md bg-indigo-950/40 border border-indigo-500/20 p-3 text-indigo-100">{t("hero_card_a")}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const FEATURES = [
  { icon: Bot, k: "f_agents" }, { icon: BookOpen, k: "f_kb" }, { icon: MessagesSquare, k: "f_chat" },
  { icon: Mic, k: "f_voice" }, { icon: Radio, k: "f_stream" }, { icon: ShieldCheck, k: "f_safety" },
];

const Features = ({ img }) => {
  const { t } = useT();
  return (
    <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
      <p className="eyebrow mb-3">{t("features_eyebrow")}</p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl">{t("features_title")}</h2>
      <div className="mt-14 grid md:grid-cols-6 gap-6">
        <div className="md:col-span-4 md:row-span-2 relative rounded-lg overflow-hidden border border-white/10 min-h-[320px]">
          <img src={img.features || DATA} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-[#09090B]/60" />
          <div className="relative p-8 flex flex-col justify-end h-full">
            <BookOpen className="h-6 w-6 text-indigo-300 mb-4" />
            <p className="font-display text-2xl font-bold">{t("f_kb_t")}</p>
            <p className="text-slate-300 mt-2 max-w-md">{t("f_kb_d")}</p>
          </div>
        </div>
        {FEATURES.filter((f) => f.k !== "f_kb").map(({ icon: Icon, k }, i) => (
          <div key={k} className={`${i < 2 ? "md:col-span-2" : "md:col-span-2"} rounded-lg border border-white/[0.08] bg-[#111118] p-7 hover:border-indigo-500/40 transition-colors`} data-testid={`feature-${k}`}>
            <Icon className="h-5 w-5 text-indigo-400" />
            <p className="font-display text-lg font-bold mt-5">{t(`${k}_t`)}</p>
            <p className="text-sm text-slate-400 mt-2">{t(`${k}_d`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const Pricing = () => {
  const { t } = useT();
  const { data: plans = [] } = useQuery({ queryKey: ["plans"], queryFn: () => axios.get(`${API}/billing/plans`).then((r) => r.data) });
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 border-t border-white/[0.06]">
      <p className="eyebrow mb-3">{t("pricing")}</p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("pricing_title")}</h2>
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {plans.filter((p) => p.price > 0).map((p) => (
          <div key={p.id} data-testid={`landing-plan-${p.id}`} className={`rounded-lg border p-8 flex flex-col ${p.id === "pro" ? "border-indigo-500/50 bg-indigo-950/20 beam" : "border-white/[0.08] bg-[#111118]"}`}>
            <p className="eyebrow">{p.name}</p>
            <p className="font-display text-5xl font-black mt-4">€{p.price}<span className="text-base font-normal text-slate-400">/{t("month")}</span></p>
            <ul className="mt-8 space-y-3 text-sm text-slate-300 flex-1">
              {[`${p.agents} ${t(p.agents === 1 ? "ai_agent" : "ai_agents")}`, `${p.documents} ${t("documents")}`, `${p.ai_messages.toLocaleString()} ${t("ai_responses_mo")}`, `${p.stream_hours} ${t("stream_hours_mo")}`].map((x) => (
                <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />{x}</li>
              ))}
            </ul>
            <Link to="/sign-up" data-testid={`landing-plan-${p.id}-btn`} className={`mt-8 rounded-full text-center py-3 font-semibold transition-colors ${p.id === "pro" ? "bg-indigo-600 hover:bg-indigo-500" : "border border-white/15 hover:bg-white/5"}`}>{t("start_now")}</Link>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Landing() {
  const { t, site, siteError, refreshSite } = useT();
  const [params] = useSearchParams();
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <AppLoading />;
  if (isSignedIn && params.get("preview") !== "1") return <Navigate to="/dashboard" replace />;
  const sections = site?.sections || [{ id: "hero", visible: true }, { id: "features", visible: true }, { id: "pricing", visible: true }];
  const img = site?.images || {};
  const render = { hero: <Hero img={img} />, features: <Features img={img} />, pricing: <Pricing /> };
  return (
    <div className="bg-[#09090B] text-slate-50 min-h-screen">
      <Nav />
      <main className="pt-16">
        {siteError && !site ? <div className="px-8 py-24" data-testid="home-load-error" role="alert">{t("site_load_error")}<button data-testid="home-retry" className="ml-3 underline" onClick={refreshSite}>{t("retry")}</button></div> : !site ? <AppLoading label={t("loading")} /> : sections.filter((s) => s.visible).map((s) => <div key={s.id} data-testid={`home-section-${s.id}`}>{render[s.id]}</div>)}
      </main>
      <footer className="border-t border-white/[0.06] py-10 text-center text-xs text-slate-500">© 2026 IAGORA AI LIVE · {t("footer")}</footer>
    </div>
  );
}
